// geo — where the phone is when a shift starts or stops.
//
// The classic app records latitude/longitude on every clock in and out; the
// rebuilt Field app never asked for them, so every shift since the cutover
// has been stored with no location (docs/REBUILD_PARITY.md, "Time tracking").
// The server has always accepted them — server/routes/field.js applyOp reads
// op.lat / op.lng — so this is the missing half.
//
// ── The rule, learned the hard way (issue #128) ─────────────────────────────
//
//   Acquiring a location must NEVER be the reason a shift did not start.
//
// The classic app once let a geolocation rejection surface as "Failed to start
// tracking" on a shift that was never attempted, leaving a worker standing in
// a yard with no way forward. So `position()` NEVER rejects and never hangs:
// every failure — denied, unsupported, insecure origin, timed out, a browser
// that ignores its own timeout — resolves to null, and the clock-in proceeds
// without coordinates. Losing a location on an entry is recoverable. Losing
// the clock-in is not.
//
// The wait is capped at GPS_TIMEOUT_MS by our own timer as well as the
// browser's, because some WebViews ignore the `timeout` option entirely and a
// crew tapping "Clock in" cannot be left watching a spinner.

//
// ── The mile-away report (2026-09-10), and why maximumAge is 0 ─────────────
//
// The owner: "noticed some locations captured show the worker a mile away from
// location, is that their phone or something on our side?"
//
// It was on our side, twice over.
//
//   1. This call asked for `maximumAge: 60000` — "a fix you already hold is
//      fine if it is under a minute old". A crew member driving to a site is a
//      MILE from where they were sixty seconds ago, so what got stored against
//      the clock-in was a real fix of a real place, the wrong one. Nothing
//      downstream could tell, because no column records a fix's AGE. For a
//      clock-in the position has to describe the moment of the tap, and the
//      only value of maximumAge that guarantees that is 0.
//
//      What that costs: the browser can no longer answer instantly from its
//      cache, so a cold GPS now takes the wait it always really needed. The
//      budget is unchanged — GPS_TIMEOUT_MS, enforced by the browser AND by
//      our own timer — and a timeout still costs the LOCATION, never the
//      shift. A crew member waits no longer than they already did when
//      location was denied; the difference is that the wait now buys a fix
//      that is true.
//
//   2. `coords.accuracy` — the radius in metres the browser is confident
//      within — was thrown away by `done({ lat, lng })`. So an 8 m satellite
//      fix and a 2 km wifi/cell triangulation were stored identically and the
//      office had no way to tell evidence from a guess. iOS makes this sharper
//      than it sounds: with Precise Location off for a site, Safari returns a
//      DELIBERATELY coarse fix, kilometres wide, with no other signal that
//      anything is wrong. "A mile away" is exactly what that looks like.
//
//      The radius rides with the coordinates from here on. THE SERVER HAS
//      NOWHERE TO PUT IT YET: time_entries is (latitude, longitude, timezone)
//      and nothing else (server/database.js), so routes/field.js applyOp reads
//      op.lat/op.lng and ignores op.accuracy. Ending that is one additive
//      column —
//
//          ALTER TABLE time_entries ADD COLUMN location_accuracy REAL
//
//      — which is a migration, and a migration ships ALONE with a human
//      (CLAUDE.md rule 3). It is not written here. What IS done here is the
//      half a phone owns: the radius is measured, carried on the wire, and
//      shown to the crew member at the moment it is taken (apps/field/
//      gpsGate.js), so a coarse fix is visible as coarse to the one person who
//      can go and stand somewhere better.

//
// ── Measured in a real browser before this shipped, 2026-09-10 ─────────────
//
// Playwright/Chromium at 390px against the built app, driving Clock in:
//
//   permission granted, a fix available   tap → the op on the wire in 118ms.
//                                         maximumAge: 0 costs a crew member
//                                         nothing when the phone can answer,
//                                         which is the ordinary case.
//   permission set to `denied` over CDP   the browser NEVER calls the error
//                                         callback. 8484ms — our own timer,
//                                         not the browser's. The cap is
//                                         load-bearing, not belt-and-braces,
//                                         and 8s is the longest anybody
//                                         waits.
//
// So the honest summary of the trade: the common path is unchanged, and the
// worst case is the 8 seconds it already was. Do not "optimise" this by
// putting maximumAge back; the number to change if a crew complains about
// waiting is GPS_TIMEOUT_MS, which costs accuracy of the ANSWER rather than
// truth of it.

export const GPS_TIMEOUT_MS = 8000;
/** Never a cached fix. See the block above — this being 60000 was the bug. */
export const GPS_MAX_AGE_MS = 0;
export const GPS_PREF_KEY = 'fw_gps';

/**
 * Why there is no fix — the part `position()` throws away.
 *
 * `position()` collapses everything to null, which is right for the sync op
 * and useless for a sentence: "turn location on for this app" and "step
 * outside, away from the metal" are different advice, and only the error code
 * separates them. `locate()` keeps the code so a screen can say the right one.
 */
export const GPS_STATUS = {
  OK: 'ok',
  OFF: 'off',                   // this person's own switch, in Me
  UNSUPPORTED: 'unsupported',   // no geolocation, or an insecure origin that throws
  DENIED: 'denied',             // PERMISSION_DENIED (1)
  UNAVAILABLE: 'unavailable',   // POSITION_UNAVAILABLE (2), or a malformed fix
  TIMEOUT: 'timeout',           // TIMEOUT (3), or our own cap firing
};

const STATUS_BY_CODE = {
  1: GPS_STATUS.DENIED,
  2: GPS_STATUS.UNAVAILABLE,
  3: GPS_STATUS.TIMEOUT,
};

/** Has the person turned location recording off? Default is on. */
export function gpsEnabled(store = safeLocalStorage()) {
  try {
    return store ? store.getItem(GPS_PREF_KEY) !== 'off' : true;
  } catch (_) {
    return true;
  }
}

export function setGpsEnabled(on, store = safeLocalStorage()) {
  try {
    if (store) store.setItem(GPS_PREF_KEY, on ? 'on' : 'off');
  } catch (_) { /* private mode: the default (on) stands for this session */ }
}

function safeLocalStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (_) {
    return null; // some browsers throw on access when site data is blocked
  }
}

/**
 * The accuracy radius in metres, or null.
 *
 * Null rather than a number is deliberate and is NOT the same as 0: a 0 would
 * read downstream as "perfect", which is the opposite of "the browser did not
 * say". A negative or non-finite value is a browser answering nonsense and is
 * treated as no answer.
 */
function accuracyOf(coords) {
  const a = coords && coords.accuracy;
  return typeof a === 'number' && Number.isFinite(a) && a >= 0 ? a : null;
}

function fixOf(pos) {
  const c = pos && pos.coords;
  if (!c || typeof c.latitude !== 'number' || typeof c.longitude !== 'number') return null;
  if (!Number.isFinite(c.latitude) || !Number.isFinite(c.longitude)) return null;
  return { lat: c.latitude, lng: c.longitude, accuracy: accuracyOf(c) };
}

/**
 * The current position AND why there is not one:
 *   { fix: { lat, lng, accuracy } | null, status: GPS_STATUS[…] }
 *
 * Never rejects. Never waits longer than `timeoutMs`.
 *
 * @param {object} [opts]
 * @param {object} [opts.geolocation]  injectable navigator.geolocation
 * @param {number} [opts.timeoutMs]
 * @param {boolean} [opts.enabled]     override the stored preference
 */
export function locate({ geolocation, timeoutMs = GPS_TIMEOUT_MS, enabled } = {}) {
  const on = enabled === undefined ? gpsEnabled() : enabled;
  if (!on) return Promise.resolve({ fix: null, status: GPS_STATUS.OFF });

  const geo = geolocation !== undefined
    ? geolocation
    : (typeof navigator !== 'undefined' ? navigator.geolocation : null);
  if (!geo || typeof geo.getCurrentPosition !== 'function') {
    return Promise.resolve({ fix: null, status: GPS_STATUS.UNSUPPORTED });
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (fix, status) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve({ fix: fix || null, status });
    };
    // Our own cap: a browser that ignores its `timeout` option must not be
    // able to hold up the clock-in indefinitely.
    const timer = setTimeout(() => done(null, GPS_STATUS.TIMEOUT), timeoutMs);

    try {
      geo.getCurrentPosition(
        (pos) => {
          const fix = fixOf(pos);
          // A fix whose numbers are junk is a phone that could not answer, not
          // a person who refused — so it reads as UNAVAILABLE, which is the
          // advice ("step outside") that might actually help.
          if (!fix) return done(null, GPS_STATUS.UNAVAILABLE);
          done(fix, GPS_STATUS.OK);
        },
        (err) => done(null, STATUS_BY_CODE[err && err.code] || GPS_STATUS.UNAVAILABLE),
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: GPS_MAX_AGE_MS },
      );
    } catch (_) {
      // Insecure origin and some old WebViews throw synchronously.
      done(null, GPS_STATUS.UNSUPPORTED);
    }
  });
}

/**
 * The current position as { lat, lng, accuracy }, or null.
 *
 * Never rejects. Never waits longer than `timeoutMs`. The REASON for a null is
 * `locate()`; this is the shape the sync op and the older callers want.
 */
export async function position(opts) {
  const { fix } = await locate(opts);
  return fix;
}

/**
 * The location fields to merge into a sync op — an EMPTY object when there is
 * no fix, so the op carries no null coordinates that would overwrite a real
 * position the server already holds (it COALESCEs on clock-out).
 *
 * `accuracy` is present only when the browser gave one, for the same reason:
 * a key that is there but null is a claim, and "we do not know" is not one.
 * The server ignores it until time_entries has a column for it — see the
 * header of this file for the one ALTER that ends that.
 */
export function fieldsFor(fix) {
  if (!fix) return {};
  const out = { lat: fix.lat, lng: fix.lng };
  if (typeof fix.accuracy === 'number' && Number.isFinite(fix.accuracy)) out.accuracy = fix.accuracy;
  return out;
}

export async function geoFields(opts) {
  return fieldsFor(await position(opts));
}
